import React, { useMemo } from "react";
import Text from "../../../common/typography/text";
import ProfileProviderIcon from "../../../icons/provider-profile-icon";
import Tags from "../../../common/tags";

const joinTextParts = (parts = [], separator = ", ") =>
  parts
    .map((part) => (typeof part === "string" ? part.trim() : part))
    .filter(Boolean)
    .join(separator);

const formatCityStateZip = (accountDetails) => {
  const location = joinTextParts(
    [accountDetails?.City, accountDetails?.State],
    ", ",
  );

  return joinTextParts([location, accountDetails?.Zip], " ");
};

const formatAddressLines = (accountDetails) => {
  const firstLine = joinTextParts([accountDetails?.AddressLine1], ", ");
  const secondLine = joinTextParts(
    [accountDetails?.AddressLine2, formatCityStateZip(accountDetails)],
    ", ",
  );

  if (firstLine) {
    return {
      firstLine,
      secondLine,
    };
  }

  return {
    firstLine: secondLine,
    secondLine: "",
  };
};

const getAssignedSalesRepName = (accountDetails) => {
  if (!accountDetails?.IsDistUserAssigned) {
    return "";
  }

  return joinTextParts([accountDetails?.DistributorUsername], " ");
};

const getContactName = (contact) =>
  typeof contact === "string" ? contact.trim() : contact?.label || "";

const renderTextOrFallback = (value, fallback = "-") =>
  value ? (
    <Text variant="bodyText" color="lightText">
      {value}
    </Text>
  ) : (
    <Text variant="bodyText" color="lightText">
      {fallback}
    </Text>
  );

const ConfirmationContent = ({
  accountName,
  shipToAccountName,
  shipToAccountDetails,
  billToAccountDetails,
  shippingContact,
  providerName,
  procedureTagText = "",
  shippingContactTagText = "",
  CaseID,
  fullName,
  applicationNumber,
  applicationDate,
  isPhone,
  requiresProcedureInfo = true,
}) => {
  const shipToAddress = useMemo(
    () => formatAddressLines(shipToAccountDetails),
    [shipToAccountDetails],
  );
  const billToAddress = useMemo(
    () => formatAddressLines(billToAccountDetails),
    [billToAccountDetails],
  );
  const assignedSalesRepName = useMemo(
    () => getAssignedSalesRepName(shipToAccountDetails),
    [shipToAccountDetails],
  );

  const confirmationDetails = useMemo(
    () => ({
      ShipTo: shipToAccountDetails?.AccountName || shipToAccountName || accountName,
      ShipAddress1: shipToAddress.firstLine,
      ShipAddress2: shipToAddress.secondLine,
      Contact: getContactName(shippingContact),
      Phone: shipToAccountDetails?.Phone || "",
      Fax: shipToAccountDetails?.Fax || "",
      BillTo: billToAccountDetails?.AccountName || accountName,
      BillAddress1: billToAddress.firstLine,
      BillAddress2: billToAddress.secondLine,
      APContact: billToAccountDetails?.APContact || "",
      APPhone: billToAccountDetails?.Phone || "",
      APFax: billToAccountDetails?.Fax || "",
      DistributorName: shipToAccountDetails?.DistributorName || "",
      SalesRep: assignedSalesRepName,
    }),
    [
      accountName,
      assignedSalesRepName,
      billToAccountDetails,
      billToAddress.firstLine,
      billToAddress.secondLine,
      shipToAccountDetails,
      shipToAccountName,
      shipToAddress.firstLine,
      shipToAddress.secondLine,
      shippingContact,
    ],
  );

  return (
    <>
      <div className="content-parent-container">
        <div
          className="confirmation-parent-wrapper"
          style={
            isPhone
              ? { width: "100%", flexDirection: "column" }
              : { width: "100%" }
          }
        >
          <div className="confirmation-detail-wrapper">
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Ship To
                </Text>
              </div>
              <div className="direct-order-review-child-content-heading-container">
                <Text variant="bodyTextBold">{confirmationDetails.ShipTo || "-"}</Text>
                {confirmationDetails.ShipAddress1 ? (
                  <Text variant="bodyText">{confirmationDetails.ShipAddress1}</Text>
                ) : null}
                {confirmationDetails.ShipAddress2 ? (
                  <Text variant="bodyText">{confirmationDetails.ShipAddress2}</Text>
                ) : null}
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Contact
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {confirmationDetails.Contact ? (
                  <div className="direct-order-review-detail-content-code-quality-container">
                    <ProfileProviderIcon className="icon-container-lightText" />
                    <Text variant="bodyText" color="lightText">
                      {confirmationDetails.Contact}
                    </Text>
                    {shippingContactTagText ? (
                      <Tags
                        tagText={shippingContactTagText}
                        tagBackground="blueBackground"
                        textColor="primaryColor"
                      />
                    ) : null}
                  </div>
                ) : (
                  renderTextOrFallback("")
                )}
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Phone
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {renderTextOrFallback(confirmationDetails.Phone)}
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Fax
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {renderTextOrFallback(confirmationDetails.Fax)}
              </div>
            </div>
          </div>
          <div className="confirmation-detail-wrapper">
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Bill To
                </Text>
              </div>
              <div className="direct-order-review-child-content-heading-container">
                <Text variant="bodyTextBold">{confirmationDetails.BillTo || "-"}</Text>
                {confirmationDetails.BillAddress1 ? (
                  <Text variant="bodyText">{confirmationDetails.BillAddress1}</Text>
                ) : null}
                {confirmationDetails.BillAddress2 ? (
                  <Text variant="bodyText">{confirmationDetails.BillAddress2}</Text>
                ) : null}
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Contact
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {confirmationDetails.APContact ? (
                  <div className="direct-order-review-detail-content-code-quality-container">
                    <ProfileProviderIcon className="icon-container-lightText" />
                    <Text variant="bodyText" color="lightText">
                      {confirmationDetails.APContact}
                    </Text>
                  </div>
                ) : (
                  renderTextOrFallback("")
                )}
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Phone
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {renderTextOrFallback(confirmationDetails.APPhone)}
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Fax
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {renderTextOrFallback(confirmationDetails.APFax)}
              </div>
            </div>
          </div>
        </div>
        <div className="direct-order-review-detail-container">
          <div className="direct-order-review-detail-child-container">
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Distributor
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                <Text variant="bodyTextBold">
                  {confirmationDetails.DistributorName || "-"}
                </Text>
              </div>
            </div>
            <div className="direct-order-review-detail-text-container">
              <div className="direct-order-review-detail-title-text-container">
                <Text variant="bodyText" color="lightText">
                  Sales Rep
                </Text>
              </div>
              <div className="direct-order-review-detail-content-text-container">
                {confirmationDetails.SalesRep ? (
                  <div className="direct-order-review-detail-content-code-quality-container">
                    <ProfileProviderIcon className="icon-container-lightText" />
                    <Text variant="bodyText" color="lightText">
                      {confirmationDetails.SalesRep}
                    </Text>
                  </div>
                ) : (
                  renderTextOrFallback("")
                )}
              </div>
            </div>
          </div>
        </div>
        {requiresProcedureInfo ? (
          <>
            <div className="direct-order-review-detail-container">
              <div className="direct-order-review-detail-child-container">
                <div className="direct-order-review-detail-text-container">
                  <div className="direct-order-review-detail-title-text-container">
                    <Text variant="bodyText" color="lightText">
                      Provider’s Name
                    </Text>
                  </div>
                  <div className="direct-order-review-detail-content-text-container">
                    {providerName?.label ? (
                      <div className="direct-order-review-detail-content-code-quality-container">
                        <ProfileProviderIcon className="icon-container-lightText" />
                        <Text variant="bodyText" color="lightText">
                          {providerName.label}
                        </Text>
                        {procedureTagText ? (
                          <Tags
                            tagText={procedureTagText}
                            tagBackground="blueBackground"
                            textColor="primaryColor"
                          />
                        ) : null}
                      </div>
                    ) : (
                      renderTextOrFallback("")
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div
              className="confirmation-parent-wrapper"
              style={
                isPhone
                  ? { width: "100%", flexDirection: "column" }
                  : { width: "100%" }
              }
            >
              <div
                className="confirmation-detail-right-border-wrapper"
                style={
                  isPhone ? {} : { borderRight: "1px solid var(--strokeLight)" }
                }
              >
                <div className="direct-order-review-detail-child-container">
                  <div className="direct-order-review-detail-text-container">
                    <div className="direct-order-review-detail-title-text-container">
                      <Text variant="bodyText" color="lightText">
                        Case ID
                      </Text>
                    </div>
                    <div className="direct-order-review-detail-content-text-container">
                      <div className="direct-order-review-detail-content-code-quality-container">
                        <Text variant="bodyTextBold">{CaseID || "-"}</Text>
                      </div>
                    </div>
                  </div>
                  <div className="direct-order-review-detail-text-container">
                    <div className="direct-order-review-detail-title-text-container">
                      <Text variant="bodyText" color="lightText">
                        Patient Code
                      </Text>
                    </div>
                    <div className="direct-order-review-detail-content-text-container">
                      <div className="direct-order-review-detail-content-code-quality-container">
                        <Text variant="bodyTextBold">{fullName || "-"}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="confirmation-detail-left-wrapper">
                <div className="direct-order-review-detail-child-container">
                  <div className="direct-order-review-detail-text-container">
                    <div className="direct-order-review-detail-title-text-container">
                      <Text variant="bodyText" color="lightText">
                        Application Date
                      </Text>
                    </div>
                    <div className="direct-order-review-detail-content-text-container">
                      <div className="direct-order-review-detail-content-code-quality-container">
                        <Text variant="bodyText">
                          {applicationDate?.format("MM/DD/YYYY") || "-"}
                        </Text>
                      </div>
                    </div>
                  </div>
                  <div className="direct-order-review-detail-text-container">
                    <div className="direct-order-review-detail-title-text-container">
                      <Text variant="bodyText" color="lightText">
                        Application #
                      </Text>
                    </div>
                    <div className="direct-order-review-detail-content-text-container">
                      <div className="direct-order-review-detail-content-code-quality-container">
                        <Text variant="bodyText">{applicationNumber || "-"}</Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </>
  );
};

export default ConfirmationContent;
